import React, { useState, useEffect, useCallback } from "react";
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/core';
import { RJSFSchema, UiSchema, WidgetProps } from '@rjsf/utils';
import { generateUISchema, applyReferenceWidgets } from "./SchemaFormTemplates/generateUISchema";
import HorizontalFieldTemplate from "./SchemaFormTemplates/HorizontalFieldTemplate";
import CustomArrayFieldTemplate from "./SchemaFormTemplates/CustomArrayFieldTemplate";
import CustomDescriptionField from "./SchemaFormTemplates/CustomDescriptionField";
import CustomFieldHelpTemplate from "./SchemaFormTemplates/CustomFieldHelpTemplate";
import { useTranslation } from "react-i18next";
import { DraftReferenceState } from "../../api/referenceOptions";

// Define the types for SchemaFormProps
type SchemaFormProps = {
  userIsAdmin: boolean,
  formTitle: string,
  schema: RJSFSchema,
  ui_schema: UiSchema,
  formdata?: Object,
  onChange: Function
  isEditable: boolean,
  errors?: any,
  integrationId?: string,
  authToken?: string,
  draftReference?: DraftReferenceState,
}

const SchemaForm = ({
  userIsAdmin,
  formTitle,
  schema,
  ui_schema,
  formdata,
  onChange,
  isEditable,
  errors,
  integrationId,
  authToken,
  draftReference,
}: SchemaFormProps) => {
  const { t } = useTranslation();
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [customSchema, setCustomSchema] = useState<UiSchema>({});
  const [formData, setFormData] = useState(formdata ? formdata : null);
  const [loading, setLoading] = useState(true); // Add loading state
  const printError = (error: any) => console.error("schema form error", { error });

  // Create a toggle password function that takes the field name as a parameter
  // We use useCallback with empty deps to ensure this function never changes its reference
  const togglePassword = useCallback((fieldName: string) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }, []);

  // Store a reference to togglePassword that can be safely used in dependency arrays
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTogglePassword = React.useMemo(() => togglePassword, []);

  // Initial setup and when dependencies change
  useEffect(() => {
    if (formData) {
      setCustomSchema(applyReferenceWidgets(generateUISchema(userIsAdmin, schema, ui_schema, errors, visiblePasswords, stableTogglePassword, formData)));
    }
  }, [userIsAdmin, schema, ui_schema, errors, formData, visiblePasswords, stableTogglePassword]);

  // Initialize form data from props, auto-selecting single-option enum fields
  useEffect(() => {
    const data: any = { ...(formdata || {}) };
    if (schema?.properties) {
      Object.keys(schema.properties).forEach((key) => {
        const prop = (schema.properties as any)[key];
        if (prop?.enum?.length === 1 && (data[key] === undefined || data[key] !== prop.enum[0])) {
          data[key] = prop.enum[0];
        }
      });
    }
    setFormData(data);
  }, [formdata, schema]);

  const handleFormChange = (newFormData: any) => {
    setFormData(newFormData); // Update form data without regenerating UI schema on every change
  };

  return (
    <div className="flex flex-col w-full" id="json-form">
      {/* Display root level description below the form title if it exists, preferring ui_schema['ui:description'] */}
      {(ui_schema && ui_schema['ui:description']) ? (
        <div className="form-root-description text-gray-600 mt-1 mb-3 transition-colors duration-300">
          {ui_schema['ui:description']}
        </div>
      ) : schema.description ? (
        <div className="form-root-description text-gray-600 mt-1 mb-3 transition-colors duration-300">
          {schema.description}
        </div>
      ) : null}
      <div className="custom-form w-full rounded transition-all duration-300">
        <Form
          key={JSON.stringify(customSchema)}
          schema={schema}
          uiSchema={{
            ...customSchema,
            // Hide the root level description in the form since we're showing it above
            'ui:description': ''
          }}
          validator={validator}
          onChange={(e, fieldId) => {
            handleFormChange(e.formData)
            // fieldId is set only for user-originated edits; rjsf's initial
            // onChange (default population on mount) omits it.
            onChange(e.formData, fieldId)
          }}
          onError={printError}
          children={true}
          formData={formData}
          // Exposed to custom widgets/fields via props.registry.formContext.
          // rootFormData mirrors the live `formData` state (kept current by
          // handleFormChange on every onChange), not the static `formdata` prop,
          // so widgets always see the current in-progress edits.
          formContext={{ rootFormData: formData, integrationId, authToken, draftReference }}
          disabled={!isEditable}
          className="rjsf-form-tailwind transition-all duration-300"
          templates={{
            FieldTemplate: HorizontalFieldTemplate,
            ArrayFieldTemplate: CustomArrayFieldTemplate,
            DescriptionFieldTemplate: CustomDescriptionField,
            FieldHelpTemplate: CustomFieldHelpTemplate
          }}
        />
      </div>
    </div>
  )
}

export default SchemaForm;
